import type { Socket } from "socket.io-client";
import { io } from "socket.io-client";
import { SOCKET_EVENT, SOCKET_IO_EVENT } from "~/composables/use-socket-io/index";

/**
 * Socket.IO Client Plugin
 *
 * .client.ts suffix ensures this only runs on client-side.
 * Provides Socket.IO instance via $socket throughout the app.
 *
 * Socket handlers (useUserSocket, useEventSocket) should be initialized
 * in component context (e.g., app.vue), not here.
 *
 * @example
 * ```typescript
 * const { $socket } = useNuxtApp();
 * if ($socket) {
 *   $socket.emit('event', data);
 * }
 * ```
 */

export default defineNuxtPlugin(() => {
  const appConfig = useRuntimeConfig().public;

  let socket: Socket | null = null;

  if (!appConfig.APP_CUSTOMER.app_config.socket_base) {
    console.warn("`SOCKET_BASE` not found! Unable to use SocketIO!");
  }
  else {
    socket = io(appConfig.APP_CUSTOMER.app_config.socket_base, {
      transports: ["websocket"],
      path: "/ws",
    });

    // eslint-disable-next-line no-console
    const log = (event: string, data: unknown) => console.log({ event, data });

    // Setup event listeners for debugging
    socket.on(SOCKET_IO_EVENT.CONNECT, () => log(SOCKET_IO_EVENT.CONNECT, undefined));
    socket.on(SOCKET_IO_EVENT.DISCONNECT, (reason: string) => log(SOCKET_IO_EVENT.DISCONNECT, reason));
    socket.on(SOCKET_EVENT.JOIN_USER_ROOM, (data: unknown) => log(SOCKET_EVENT.JOIN_USER_ROOM, data));
    socket.on(SOCKET_EVENT.LEAVE_USER_ROOM, (data: unknown) => log(SOCKET_EVENT.LEAVE_USER_ROOM, data));
    socket.on(SOCKET_EVENT.REMOVE_USER_DEVICE, (data: unknown) => log(SOCKET_EVENT.REMOVE_USER_DEVICE, data));
    socket.on(SOCKET_EVENT.EVENT_URL_CHANGE, (data: unknown) => log(SOCKET_EVENT.EVENT_URL_CHANGE, data));
    socket.on(SOCKET_EVENT.UPDATE_EVENT, (data: unknown) => log(SOCKET_EVENT.UPDATE_EVENT, data));
  }

  return {
    provide: {
      socket,
    },
  };
});
